import { FingerType } from '@/types';
import { FingerAbbreviation } from '@/types/wordTraining';

/**
 * Generate key with hand suffix for word training
 */
export const mapFingersToKeyWithHand = (
  fingers: FingerType[], 
  hand: 'left' | 'right'
): string => {
  const baseKey = mapFingersToKey(fingers);
  return `${baseKey}_${hand}`;
};

/**
 * Determines hand from finger combination (enhanced for word training)
 */
export const getHandFromFingersForWordTraining = (fingers: FingerType[]): 'left' | 'right' => {
  if (!fingers || fingers.length === 0) {
    return 'left'; // Default fallback
  }

  const hasLeft = fingers.some(finger => finger.startsWith('left'));
  const hasRight = fingers.some(finger => finger.startsWith('right'));

  // For word training, we need to pick one hand explicitly
  // Priority: if both hands have fingers, prefer left for consistency
  if (hasLeft) return 'left';
  if (hasRight) return 'right';
  
  return 'left'; // Fallback
};

/**
 * Finger Combination Mapper
 * 
 * Utilities for converting between FingerType arrays and finger abbreviation
 * combinations for the word training system.
 */

// Mapping from FingerType to abbreviation
const FINGER_TO_ABBREVIATION: Record<FingerType, FingerAbbreviation> = {
  leftThumb: 'T',
  leftPointing: 'I',
  leftMiddle: 'M',
  leftRing: 'R',
  leftPinky: 'P',
  rightThumb: 'T',
  rightPointing: 'I',
  rightMiddle: 'M',
  rightRing: 'R',
  rightPinky: 'P'
};

// Reverse mapping from abbreviation to FingerType (left hand first)
const ABBREVIATION_TO_FINGER: Record<FingerAbbreviation, { left: FingerType; right: FingerType }> = {
  T: { left: 'leftThumb', right: 'rightThumb' },
  I: { left: 'leftPointing', right: 'rightPointing' },
  M: { left: 'leftMiddle', right: 'rightMiddle' },
  R: { left: 'leftRing', right: 'rightRing' },
  P: { left: 'leftPinky', right: 'rightPinky' }
};

/**
 * Converts FingerType array to finger abbreviation key
 */
export const mapFingersToKey = (fingers: FingerType[]): string => {
  if (!fingers || fingers.length === 0) {
    throw new Error('No fingers provided for mapping');
  }

  // Extract unique finger abbreviations
  const abbreviations = new Set<FingerAbbreviation>();
  
  fingers.forEach(finger => {
    const abbrev = FINGER_TO_ABBREVIATION[finger];
    if (abbrev) {
      abbreviations.add(abbrev);
    }
  });

  if (abbreviations.size === 0) {
    throw new Error('No valid finger abbreviations found');
  }

  // Sort abbreviations in standard order: T, I, M, R, P
  const sortOrder: Record<FingerAbbreviation, number> = { T: 0, I: 1, M: 2, R: 3, P: 4 };
  const sortedAbbreviations = Array.from(abbreviations).sort((a, b) => sortOrder[a] - sortOrder[b]);
  
  return sortedAbbreviations.join('_');
};

/**
 * Converts finger abbreviation array to key
 */
export const mapAbbreviationsToKey = (abbreviations: FingerAbbreviation[]): string => {
  if (!abbreviations || abbreviations.length === 0) {
    throw new Error('No abbreviations provided for mapping');
  }

  // Remove duplicates and sort
  const uniqueAbbrevs = Array.from(new Set(abbreviations));
  const sortOrder: Record<FingerAbbreviation, number> = { T: 0, I: 1, M: 2, R: 3, P: 4 };
  const sortedAbbrevs = uniqueAbbrevs.sort((a, b) => sortOrder[a] - sortOrder[b]);
  
  return sortedAbbrevs.join('_');
};

/**
 * Validates finger combination for word training
 */
export const validateFingerCombination = (combination: string[]): boolean => {
  if (!combination || combination.length === 0) {
    return false;
  }

  if (combination.length > 5) {
    return false; // Maximum 5 different finger types
  }

  // Check if all are valid abbreviations
  const validAbbrevs: FingerAbbreviation[] = ['T', 'I', 'M', 'R', 'P'];
  return combination.every(abbrev => validAbbrevs.includes(abbrev as FingerAbbreviation));
};

/**
 * Determines hand usage from finger combination
 */
export const getHandFromFingers = (fingers: FingerType[]): 'left' | 'right' | 'both' => {
  if (!fingers || fingers.length === 0) {
    return 'both'; // Default fallback
  }

  const hasLeft = fingers.some(finger => finger.startsWith('left'));
  const hasRight = fingers.some(finger => finger.startsWith('right'));

  if (hasLeft && hasRight) return 'both';
  if (hasLeft) return 'left';
  if (hasRight) return 'right';
  
  return 'both'; // Fallback
};

/**
 * Converts finger abbreviations back to FingerType arrays
 */
export const abbreviationsToFingerTypes = (
  abbreviations: FingerAbbreviation[], 
  handMode: 'left' | 'right' | 'both' = 'both'
): FingerType[] => {
  const fingers: FingerType[] = [];

  abbreviations.forEach(abbrev => {
    const fingerPair = ABBREVIATION_TO_FINGER[abbrev];
    if (fingerPair) {
      if (handMode === 'left') {
        fingers.push(fingerPair.left);
      } else if (handMode === 'right') {
        fingers.push(fingerPair.right);
      } else {
        // both hands
        fingers.push(fingerPair.left, fingerPair.right);
      }
    }
  });

  return fingers;
};

/**
 * Gets all possible finger combinations for the word training system
 */
export const getAllFingerCombinations = (): string[] => {
  const abbreviations: FingerAbbreviation[] = ['T', 'I', 'M', 'R', 'P'];
  const combinations: string[] = [];

  // Generate all possible combinations (1 to 5 fingers)
  for (let length = 1; length <= 5; length++) {
    const combos = generateCombinations(abbreviations, length);
    combos.forEach(combo => {
      combinations.push(mapAbbreviationsToKey(combo));
    });
  }

  return combinations;
};

/**
 * Helper function to generate combinations
 */
function generateCombinations<T>(array: T[], length: number): T[][] {
  if (length === 1) {
    return array.map(item => [item]);
  }

  const combinations: T[][] = [];
  
  for (let i = 0; i <= array.length - length; i++) {
    const first = array[i];
    const rest = generateCombinations(array.slice(i + 1), length - 1);
    
    rest.forEach(combo => {
      combinations.push([first, ...combo]);
    });
  }

  return combinations;
}

/**
 * Parses CSV finger combination string
 */
export const parseCSVFingerCombination = (csvString: string): FingerAbbreviation[] => {
  if (!csvString || typeof csvString !== 'string') {
    throw new Error('Invalid CSV finger combination string');
  }

  const parts = csvString.split(',').map(part => part.trim().toUpperCase() as FingerAbbreviation);
  
  // Validate all parts are valid abbreviations
  const validAbbrevs: FingerAbbreviation[] = ['T', 'I', 'M', 'R', 'P'];
  const invalidParts = parts.filter(part => !validAbbrevs.includes(part));
  
  if (invalidParts.length > 0) {
    throw new Error(`Invalid finger abbreviations: ${invalidParts.join(', ')}`);
  }

  return parts;
};

/**
 * Gets display name for finger combination
 */
export const getFingerCombinationDisplayName = (fingerKey: string): string => {
  const parts = fingerKey.split('_');
  const names: Record<FingerAbbreviation, string> = {
    T: 'Thumb',
    I: 'Index',
    M: 'Middle', 
    R: 'Ring',
    P: 'Pinky'
  };

  return parts.map(part => names[part as FingerAbbreviation] || part).join(' + ');
};

/**
 * Gets finger combination count by type
 */
export const getFingerCombinationStats = () => {
  const allCombinations = getAllFingerCombinations();
  
  const stats = {
    singleFinger: allCombinations.filter(combo => !combo.includes('_')).length,
    twoFinger: allCombinations.filter(combo => combo.split('_').length === 2).length,
    threeFinger: allCombinations.filter(combo => combo.split('_').length === 3).length,
    fourFinger: allCombinations.filter(combo => combo.split('_').length === 4).length,
    fiveFinger: allCombinations.filter(combo => combo.split('_').length === 5).length,
    total: allCombinations.length
  };

  return stats;
};
